<template>
    <span 
        class="loader" 
        :style="styles"
        :class="{ complete, random: props.mode === 'random' }"
    ></span>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'

const props = defineProps({
    duration: { type: Number, default: 60 },
    complete: { type: Boolean, default: false },
    mode:     { type: String, default: 'linear' } // 'linear' | 'random'
})

const randomWidth = ref(0)

let interval = null

function startRandom() {
    interval = setInterval(() => {
        if (randomWidth.value >= 90) {
            clearInterval(interval)
            return
        }

        // ~35% chance to stall (do nothing this tick)
        if (Math.random() < 0.35) return

        const jump = Math.random() * 9.5 + 0.5
        randomWidth.value = Math.min(90, randomWidth.value + jump)
    }, 1_000)
}

function stopRandom() {
    if (interval) clearInterval(interval)
    randomWidth.value = 100
}

watch(() => props.mode, (val) => {
    if (val === 'random') startRandom()
}, { immediate: true })

watch(() => props.complete, (val) => {
    if (val) stopRandom()
})

onUnmounted(() => {
    if (interval) clearInterval(interval)
})

const styles = computed(() => {
    if (props.mode === 'random') {
        return { '--width': `${randomWidth.value}%` }
    }
    return { '--duration': `${props.duration}s` }
})
</script>

<style lang="scss" scoped>
.loader {
    display: block;
    position: relative;
    height: 12px;
    width: 80%;
    border: 1px solid #000;
    border-radius: 10px;
    overflow: hidden;
    background-color: rgba($pale-blue, .15);
}

.loader:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 0;
    background: linear-gradient(
        90deg,
        $pale-blue 0%,
        $pale-blue 60%,
        lighten($pale-blue, 20%) 75%,
        $pale-blue 90%
    );
    background-size: 200% 100%;
    animation: 
        var(--duration, 0s) prog ease-out forwards,
        3s shimmer linear infinite;
}

/* Random mode */
.loader.random:after {
    animation: 3s shimmer linear infinite;
    width: var(--width, 0%);
    transition: width 0.4s ease;
}

/* Complete */
.loader.complete:after {
    animation: none;
    width: 100%;
    transition: width 0.4s ease;
}

@keyframes prog {
    to { width: 90%; }
}

@keyframes shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}
</style>